import { Button } from '@/components/ui/button'
import { Link } from 'react-router-dom'
import { relativeDate, ticketTitle } from '@/lib/taskRules'
import {
  daysAgoISODate,
  EmptyNote,
  RowLink,
  RowSkeleton,
  Section,
  StatTile,
  TileGrid,
} from './shared'
import type { useTaskBuckets } from './useTaskBuckets'

type Buckets = ReturnType<typeof useTaskBuckets>

const SUBMITTED_WINDOW_DAYS = 7

/**
 * Technician home: one section per question a technician actually opens the app
 * with — what was I doing, what can I pick up, what came back, what did I ship.
 *
 * Every count is scoped to me (`useTaskBuckets` runs `isAssignedToUser`) except
 * "Available to claim", which is by definition not mine yet.
 */
export function TechnicianHome({ buckets }: { buckets: Buckets }) {
  return (
    <>
      <ContinueWhereYouLeftOff buckets={buckets} />
      <TechnicianTiles buckets={buckets} />
    </>
  )
}

function ContinueWhereYouLeftOff({ buckets }: { buckets: Buckets }) {
  const { inProgress, isLoading } = buckets
  return (
    <Section
      title="Continue where you left off"
      description="Tasks you've claimed and haven't submitted yet. The claim is held by a heartbeat while you work."
      action={
        <Button asChild variant="outline" size="sm">
          <Link to="/work?tab=progress">Open in progress</Link>
        </Button>
      }
    >
      {isLoading ? (
        <RowSkeleton rows={2} />
      ) : inProgress.length === 0 ? (
        <EmptyNote>
          Nothing in progress. Claim a task from{' '}
          <Link to="/work?tab=available" className="underline underline-offset-2">
            Available
          </Link>{' '}
          and it will appear here until you sign and submit it.
        </EmptyNote>
      ) : (
        <div className="space-y-2">
          {inProgress.slice(0, 5).map((t) => (
            <RowLink
              key={t.id}
              to={`/work?tab=progress&task=${t.id}`}
              primary={ticketTitle(t)}
              secondary={<span className="font-mono">{t.study_uid}</span>}
              right={relativeDate(t.updated_at)}
            />
          ))}
        </div>
      )}
    </Section>
  )
}

function TechnicianTiles({ buckets }: { buckets: Buckets }) {
  const { available, rework, submitted, isLoading } = buckets
  const from = daysAgoISODate(SUBMITTED_WINDOW_DAYS)
  const submittedThisWeek = submitted.filter((t) => t.updated_at.slice(0, 10) >= from)

  return (
    <Section
      title="Your queue at a glance"
      description="Each number opens the list it counts — already filtered."
    >
      <TileGrid>
        <StatTile
          to="/work?tab=available"
          label="Available to claim"
          value={available.length}
          hint="Unclaimed tasks you're allowed to take."
          emptyHint="Nothing to claim right now. New work appears here as maintainers start studies."
          loading={isLoading}
        />
        <StatTile
          to="/work?tab=progress"
          label="Rework needed"
          value={rework.length}
          hint="A reviewer sent these back with a remark. Read the remark on the card before re-opening the editor."
          emptyHint="No rework outstanding — nothing you submitted has come back."
          tone="attention"
          loading={isLoading}
        />
        <StatTile
          to={`/activity?status=submitted&from=${from}`}
          label={`Submitted (last ${SUBMITTED_WINDOW_DAYS} days)`}
          value={submittedThisWeek.length}
          hint="Signed and sent — waiting on a reviewer's verdict."
          emptyHint={`Nothing submitted in the last ${SUBMITTED_WINDOW_DAYS} days. Your full history is on Activity.`}
          loading={isLoading}
        />
      </TileGrid>
    </Section>
  )
}
